import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { useTranslation } from '@lib/useTypedTranslation';

import { DeviceInfoContext } from '../../../../device';
import { DeviceEventsTimelineChart } from '../charts/deviceEventsTimelineChart';
import { filterDeviceEvents, getDeviceEvents } from '../../../../device/compositions/performance/events/deviceEvents';
import { getActiveEventFilters } from '../../../../device/compositions/performance/events/edit-events';
import { getSelectedTzDateDeviceEventsList } from '../../../../../../services/core/devicePerformance';
import { DeviceEventName, TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { useWorldRequest } from '../../../../../../lib/useWorldRequest';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { useWorldSettingsContext } from '../../../../../../context/worldSettings';
import { Loading } from '../../../../../../components/loading/loading';
import { italicAxisTitle } from '../../../../../../components/chart/lib';
import { useAnomalyExaminationEventFilters } from '../useAnomalyExaminationEventFilters';
import { AnomalySectionProps } from './index';

// See lowPowerSection.tsx's own import of this same file for why it's needed - .selectedDateCharts_container
// only gets its designed chart-container height from charts.css rules scoped under this ancestor class.
import '../../../../device/compositions/performance/reports.css';

export interface EventTimelineSectionProps extends AnomalySectionProps {
  /** Seeded (and applied) event filters this section's timeline starts with - e.g. ['deviceRebooted']
   * for RebootsSection, ['deviceDropped'] for DropsSection. */
  initiallyEnabledEvents: readonly DeviceEventName[],
  /** Events the user cannot disable. The filter hook also enables these initially. */
  lockedEvents: readonly DeviceEventName[],
  /** Forwarded to DeviceEventsTimelineChart - identifies which caller's chart a cypress test is
   * looking at, since DeviceEventsTimelineChart itself has no fixed identity of its own. */
  cypressId: string
}

/**
 * Generic "events timeline for the selected day" section, shared by every anomaly type whose
 * examination page section is just a locked event marker plus a same-day timeline of other
 * events (RebootsSection, DropsSection). Extracted from what was originally RebootsSection's own
 * body - see sibling sections for their own thin, type-specific wrappers around this component.
 */
export function EventTimelineSection(props: EventTimelineSectionProps) {
  const { deviceId, selectedDate, onChartMount, initiallyEnabledEvents, lockedEvents, cypressId } = props;
  const { t } = useTranslation(['anomalyDetectionBeta', 'translation']);
  const { platformType, timeZone } = useContext(DeviceInfoContext);
  const featureToggles = useFeatureTogglesContext();
  const { worldSettings } = useWorldSettingsContext();

  const { eventFilters, dispatchEventFilters } = useAnomalyExaminationEventFilters(
    platformType, initiallyEnabledEvents, lockedEvents
  );

  // World-level anomaly-detection time zone selectedDate was bucketed by (see
  // deviceAnomalyEventChart.ts/fillingInDataForDays.ts) - defaults to 'UTC' to match
  // deviceAnomalyExaminationPage.tsx's own fallback for the top chart.
  const anomalyTz = worldSettings.smartGroupingTimeZone || 'UTC';

  const selectedDateUtc = useMemo(() => {
    const selectedDay = moment.utc(selectedDate).format('YYYY-MM-DD');
    return {
      from: moment.tz(selectedDay, anomalyTz).startOf('day').valueOf(),
      to: moment.tz(selectedDay, anomalyTz).endOf('day').valueOf()
    };
  }, [selectedDate, anomalyTz]);

  const deviceTimeZone = timeZone?.name || 'UTC';

  // The title renders the real UTC bounds in the device zone. A world anomaly day can span more
  // than one device-local date, and a DST transition can make it 23 or 25 real hours long.
  const timelineTitle = t('DEVICE_EVENTS_TIMELINE_FOR_RANGE', {
    ns: 'anomalyDetectionBeta',
    from: moment.utc(selectedDateUtc.from).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    to: moment.utc(selectedDateUtc.to).tz(deviceTimeZone).format('Do MMMM HH:mm'),
    timeZone: timeZone?.name || 'UTC'
  });

  // Same axis-title convention lowPowerSection.tsx (the low power anomaly examination page's own
  // battery chart) already uses for labelling an axis with the device's own time zone.
  const xAxisTitle = italicAxisTitle(t('DEVICE_LOCAL_TIME', { ns: 'translation', timeZone: timeZone?.name ? ` (${timeZone.name})` : '' }));

  const eventNames = useMemo(() => Object.values(getDeviceEvents(platformType, featureToggles)).map(event => event.name), [platformType, featureToggles]);

  const dataFetcher = useCallback(() => {
    return getSelectedTzDateDeviceEventsList({ deviceId, to: selectedDateUtc.to, tz: anomalyTz, events: eventNames });
  }, [deviceId, selectedDateUtc.to, anomalyTz, eventNames]);

  const { loading, data: rawEvents } = useWorldRequest<TzDeviceEvent[]>(dataFetcher, { initialLoading: true, initialData: [] });


  // See lowPowerSection.tsx's identical hasBecomeVisible/hideChildren pattern for why this is
  // needed: DeviceEventsTimelineChart is mounted immediately (hidden) while this section's own
  // data is loading.
  const [hasBecomeVisible, setHasBecomeVisible] = useState(false);
  useEffect(() => {
    if (!loading) {
      setHasBecomeVisible(true);
    }
  }, [loading]);

  // Mirrors lowPowerSection.tsx's own deferred-registration pattern: registering this chart with
  // the shell's shared useAlignCharts group immediately at mount (while still hidden above) would
  // capture a degenerate zero-size measurement and permanently lock the shared width down to it.
  // Deferring until hasBecomeVisible avoids that; onMount itself still fires normally (and exactly
  // once) at genuine component mount, only capturing the chart instance.
  // Registration was previously skipped entirely here, because useAlignCharts' explicit y-axis
  // width sync (axis.width = maxWidth) hung amCharts (and the whole tab) once applied to this
  // chart's y-axis when that axis had its grid/labels/ticks/line renderer templates disabled -
  // disabling them skips the real measurement codepath entirely, which combined badly with the
  // forced width. See deviceEventsTimelineChart.tsx's own createChart for why the y-axis is now a
  // genuinely real axis (same as DeviceChargeLevel's), hidden only via axis.opacity = 0, which is
  // what makes registering here safe again.
  const chartRef = useRef<am4charts.XYChart | null>(null);
  const handleChartMount = useCallback((chart: am4charts.XYChart) => {
    chartRef.current = chart;
    return chart;
  }, []);
  useEffect(() => {
    if (hasBecomeVisible && chartRef.current) {
      onChartMount(chartRef.current);
    }
  }, [hasBecomeVisible, onChartMount]);

  const activeEvents = useMemo(() => getActiveEventFilters(eventFilters.appliedEventFilters), [eventFilters.appliedEventFilters]);
  const [timelineEvents] = useMemo(() => filterDeviceEvents(rawEvents, activeEvents), [rawEvents, activeEvents]);

  return (
    <div className="selectedDateCharts_container device-anomaly-examination-page__timeline-chart">
      <Loading isLoading={loading} transparentOverlay={false} hideChildren={true}>
        <DeviceEventsTimelineChart
          dateRange={selectedDateUtc}
          displayTimeZone={deviceTimeZone}
          events={timelineEvents}
          eventFilters={eventFilters}
          dispatchEventFilters={dispatchEventFilters}
          lockedEvents={lockedEvents}
          visible={hasBecomeVisible}
          onMount={handleChartMount}
          title={timelineTitle}
          xAxisTitle={xAxisTitle}
          cypressId={cypressId}
        />
      </Loading>
    </div>
  );
}
